import { useState, useCallback, useRef, useEffect } from 'react';
import { updateFamilyCompletion } from '../utils/familyCompletion';
import { getVersesForChapter } from '../utils/bibleData';

/**
 * Custom hook for managing plan progress, badge system, and family completion
 * Handles plan advancement, badge awarding, and progress tracking
 */
export const usePlanManagement = ({
  currentUser,
  selectedBook,
  selectedChapter,
  startVerse,
  endVerse,
  existingRecordings,
  onPlanAdvancement,
  setSelectedBook,
  setSelectedChapter,
  setStartVerse,
  setEndVerse
}) => {
  // Plan state
  const [currentPlanName, setCurrentPlanName] = useState(null);
  const [userHasManuallyChanged, setUserHasManuallyChanged] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completedPlanData, setCompletedPlanData] = useState(null);
  const isAdvancingRef = useRef(false);
  const lastCompletedPlanIdRef = useRef(null);
  // Set true right after the user manually picks a book/chapter from the
  // cassette dropdowns; cleared once the verse range below has been defaulted
  // to [next unrecorded verse in that chapter, last verse in that chapter].
  const chapterManuallyPickedRef = useRef(false);

  // Badge checking — delegates to the centralized badgeSystem utility
  const checkAndAwardBadges = useCallback(async (userId, db) => {
    const { checkAndAwardBadges: check } = await import('../utils/badgeSystem');
    await check(userId, db);
  }, []);

  // Function to unsubscribe user from recording plan
  const unsubscribeFromRecordingPlan = async (userId, db) => {
    try {
      console.log(`📤 UNSUBSCRIBING: Unsubscribing user ${userId} from recording plan`);
      
      // Import Firebase functions
      const { collection, getDocs, deleteDoc, doc } = await import('firebase/firestore');
      
      // Get user's recording plans
      const userRecordingPlansSnapshot = await getDocs(
        collection(db, `users/${userId}/recordingPlans`)
      );
      
      // Delete all recording plan subscriptions
      for (const planDoc of userRecordingPlansSnapshot.docs) {
        await deleteDoc(planDoc.ref);
        console.log(`✅ UNSUBSCRIBING: Deleted recording plan subscription: ${planDoc.id}`);
      }
      
      console.log(`✅ UNSUBSCRIBING: Successfully unsubscribed user from all recording plans`);
      
    } catch (error) {
      console.error('Error unsubscribing from recording plan:', error);
    }
  };

  // Function to archive a completed plan for the family
  const archiveCompletedPlan = async (planId, userId, db) => {
    try {
      console.log(`📦 ARCHIVING: Archiving completed plan ${planId} for user ${userId}`);
      
      // Import Firebase functions
      const { collection, getDocs, query, where, updateDoc, doc, addDoc, serverTimestamp, getDoc } = await import('firebase/firestore');
      
      // Get user's family IDs
      const userDoc = await getDoc(doc(db, 'users', userId));
      const userData = userDoc.data();
      const familyIds = userData.familyIds || [];
      
      if (familyIds.length === 0) {
        console.log('📦 ARCHIVING: User has no family IDs, skipping family archiving');
        return;
      }
      
      // Get the plan data to get plan name and chapters
      const planDoc = await getDoc(doc(db, 'plans', planId));
      if (!planDoc.exists()) {
        console.log('📦 ARCHIVING: Plan document not found');
        return;
      }
      
      const planData = planDoc.data();
      const planChapters = planData.chapters || [];
      
      // Archive for each family the user belongs to
      for (const familyId of familyIds) {
        console.log(`📦 ARCHIVING: Archiving plan for family ${familyId}`);
        
        // Create the family completed plan document
        const familyCompletedPlan = {
          planId: planId,
          planName: planData.name,
          planDescription: planData.description,
          totalChapters: planChapters.length,
          completedChapters: planChapters.map(chapter => `${chapter.book}_${chapter.chapter}`),
          status: 'completed',
          completedAt: serverTimestamp(),
          archivedAt: serverTimestamp(),
          currentlySubscribed: [], // Array of user IDs currently subscribed
          formerlySubscribed: [], // Array of user IDs who were previously subscribed
          currentlyListening: [], // Array of user IDs currently listening
          formerlyListening: [] // Array of user IDs who were previously listening
        };
        
        // Add to family's completed plans collection
        await addDoc(collection(db, `families/${familyId}/completedPlans`), familyCompletedPlan);
        
        console.log(`✅ ARCHIVING: Successfully archived plan ${planId} for family ${familyId}`);
      }
      
      // Update the main plan document with completion timestamp
      await updateDoc(doc(db, 'plans', planId), {
        completedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      // Update the user's recording plan status to archived
      const { collection: userCollection, getDocs: getUserDocs, updateDoc: updateUserDoc } = await import('firebase/firestore');
      const userRecordingPlansSnapshot = await getUserDocs(
        userCollection(db, `users/${userId}/recordingPlans`)
      );
      
      for (const planDoc of userRecordingPlansSnapshot.docs) {
        const planData = planDoc.data();
        if (planData.planId === planId) {
          await updateUserDoc(planDoc.ref, {
            status: 'archived',
            archivedAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
          console.log(`✅ ARCHIVING: Updated user recording plan status to archived for plan ${planId}`);
          break;
        }
      }
      
      console.log(`✅ ARCHIVING: Successfully archived plan ${planId} for all families`);
      
    } catch (error) {
      console.error('Error archiving completed plan:', error);
    }
  };


  // Plan progress tracking function
  const updatePlanProgress = useCallback(async (verseDocuments, userId, db) => {
    try {
      console.log(`📖 UPDATE PLAN PROGRESS: Starting for user ${userId}`);
      console.log(`📖 UPDATE PLAN PROGRESS: Processing ${verseDocuments.length} verse documents`);
      
      // Import Firebase functions
      const { collection, getDocs, doc, getDoc, updateDoc, serverTimestamp, query, where } = await import('firebase/firestore');
      
      // Get user's recording plans
      const recordingPlansSnapshot = await getDocs(
        collection(db, `users/${userId}/recordingPlans`)
      );
      
      console.log(`📖 UPDATE PLAN PROGRESS: Found ${recordingPlansSnapshot.docs.length} recording plans`);
      
      if (recordingPlansSnapshot.docs.length === 0) {
        console.log('📖 No recording plans found for user');
        return;
      }

      const activePlanDoc = recordingPlansSnapshot.docs.find(d => d.data().status === 'active');
      if (!activePlanDoc) {
        console.log('📖 No active recording plan found for user');
        return;
      }

      const recordingPlan = activePlanDoc;
      const planData = recordingPlan.data();
      
      // Get the actual plan data
      const planDoc = await getDoc(doc(db, 'plans', planData.planId));
      if (!planDoc.exists()) {
        console.log('📖 Plan document not found');
        return;
      }
      
      const plan = planDoc.data();
      const planChapters = plan.chapters || [];
      
      // Get user's family IDs to aggregate progress from all family members
      const userDoc = await getDoc(doc(db, 'users', userId));
      const userData = userDoc.data();
      const userFamilyIds = userData.familyIds || [];
      
      console.log('📊 User family IDs:', userFamilyIds);
      
      // Query recordings from ALL family members (not just current user)
      let allFamilyRecordings = [];
      if (userFamilyIds.length > 0) {
        const familyRecordingsSnapshot = await getDocs(
          query(
            collection(db, 'recordings'),
            where('familyIds', 'array-contains-any', userFamilyIds)
          )
        );
        allFamilyRecordings = familyRecordingsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log('📊 Found recordings from all family members:', allFamilyRecordings.length);
      } else {
        console.log('📊 User has no family IDs, using only user recordings');
        allFamilyRecordings = verseDocuments;
      }
      
      // Process each plan chapter to check completion status
      const currentProgress = planData.chapterProgress || {};
      const completedChapters = planData.completedChapters || [];
      let currentChapterIndex = planData.currentChapterIndex || 0;
      
      console.log(`📖 UPDATE PLAN PROGRESS: Current progress:`, currentProgress);
      console.log(`📖 UPDATE PLAN PROGRESS: Completed chapters:`, completedChapters);
      console.log(`📖 UPDATE PLAN PROGRESS: Processing ${planChapters.length} plan chapters`);
      
      for (const planChapter of planChapters) {
        const chapterKey = `${planChapter.book}_${planChapter.chapter}`;
        console.log(`📖 Processing plan chapter: ${chapterKey}`);
        console.log(`📖 Plan chapter details:`, planChapter);
        
        // Get all recordings for this specific chapter from family members
        const chapterRecordings = allFamilyRecordings.filter(recording => 
          recording.book === planChapter.book && recording.chapter === planChapter.chapter
        );
        
        console.log(`📊 Found ${chapterRecordings.length} recordings for ${chapterKey}`);
        
        // Initialize chapter progress if it doesn't exist
        const chapterProgress = currentProgress[chapterKey] || {
          recordedVerses: 0,
          isComplete: false,
          lastRecordedAt: null,
          recordedVerseNumbers: []
        };
        
        // Reset and recount all recorded verses for this chapter
        const recordedVerseNumbers = new Set();
        let lastRecordedAt = null;
        
        for (const recording of chapterRecordings) {
          recordedVerseNumbers.add(recording.verse);
          if (!lastRecordedAt || new Date(recording.createdAt?.toDate?.() || recording.createdAt) > new Date(lastRecordedAt)) {
            lastRecordedAt = recording.createdAt;
          }
        }
        
        // Update chapter progress with all recorded verses
        chapterProgress.recordedVerseNumbers = Array.from(recordedVerseNumbers).sort((a, b) => a - b);
        chapterProgress.recordedVerses = chapterProgress.recordedVerseNumbers.length;
        chapterProgress.lastRecordedAt = lastRecordedAt || serverTimestamp();
        
        // Get actual verse count from Bible data
        const { getVersesForChapter } = await import('../utils/bibleData');
        const actualTotalVerses = getVersesForChapter(planChapter.book, planChapter.chapter).length;
        
        console.log(`📊 Chapter ${chapterKey}: ${chapterProgress.recordedVerses}/${actualTotalVerses} verses recorded`);
        console.log(`📊 Recorded verses: [${chapterProgress.recordedVerseNumbers.join(', ')}]`);
        
        // Check if chapter is complete using actual Bible data
        const wasComplete = chapterProgress.isComplete;
        chapterProgress.isComplete = chapterProgress.recordedVerses >= actualTotalVerses;
        
        if (chapterProgress.isComplete && !wasComplete) {
          console.log(`🎉 Chapter ${chapterKey} is now complete!`);
        } else if (chapterProgress.isComplete) {
          console.log(`✅ Chapter ${chapterKey} remains complete`);
        } else {
          console.log(`⏳ Chapter ${chapterKey} not yet complete: ${chapterProgress.recordedVerses}/${actualTotalVerses}`);
        }
        
        // Update the chapter progress
        currentProgress[chapterKey] = chapterProgress;
        
        // Update completed chapters list
        console.log(`📖 Checking completion status for ${chapterKey}:`);
        console.log(`📖 - isComplete: ${chapterProgress.isComplete}`);
        console.log(`📖 - already in completedChapters: ${completedChapters.includes(chapterKey)}`);
        console.log(`📖 - current completedChapters: [${completedChapters.join(', ')}]`);
        
        if (chapterProgress.isComplete && !completedChapters.includes(chapterKey)) {
          completedChapters.push(chapterKey);
          console.log(`✅ Added ${chapterKey} to completed chapters`);
          console.log(`✅ New completedChapters: [${completedChapters.join(', ')}]`);
        } else if (!chapterProgress.isComplete && completedChapters.includes(chapterKey)) {
          // Remove from completed if no longer complete
          const index = completedChapters.indexOf(chapterKey);
          if (index > -1) {
            completedChapters.splice(index, 1);
            console.log(`❌ Removed ${chapterKey} from completed chapters (no longer complete)`);
            console.log(`❌ New completedChapters: [${completedChapters.join(', ')}]`);
          }
        } else {
          console.log(`📖 No change needed for ${chapterKey} in completed chapters`);
        }
        
        // Update current chapter index
        const chapterIndex = planChapters.findIndex(chapter => 
          chapter.book === planChapter.book && chapter.chapter === planChapter.chapter
        );
        
        if (chapterIndex >= 0 && chapterIndex > currentChapterIndex) {
          currentChapterIndex = chapterIndex;
        }
      }
      
      // Update the recording plan document with all changes
      console.log(`📖 UPDATE PLAN PROGRESS: About to save to Firestore:`);
      console.log(`📖 - chapterProgress:`, currentProgress);
      console.log(`📖 - completedChapters: [${completedChapters.join(', ')}]`);
      console.log(`📖 - currentChapterIndex: ${currentChapterIndex}`);
      
      await updateDoc(recordingPlan.ref, {
        chapterProgress: currentProgress,
        completedChapters: completedChapters,
        currentChapterIndex: currentChapterIndex,
        lastRecordedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      console.log(`✅ UPDATE PLAN PROGRESS: Successfully saved to Firestore`);
      console.log(`📊 Final completed chapters: [${completedChapters.join(', ')}]`);
      
      // Check if entire plan is complete
      const totalChapters = planChapters.length;
      
      if (completedChapters.length >= totalChapters) {
        console.log(`🎉 Plan "${plan.name}" is now complete!`);
        
        // Update plan status
        await updateDoc(recordingPlan.ref, {
          status: 'completed',
          completedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        
        // Update the main plan document with completion timestamp
        await updateDoc(doc(db, 'plans', planData.planId), {
          completedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        
        // Archive the completed plan for the family
        await archiveCompletedPlan(planData.planId, userId, db);
        
        console.log(`🏆 Plan "${plan.name}" is now available for listening subscriptions!`);
      }
      
    } catch (error) {
      console.error('Error updating plan progress:', error);
      // Don't throw error - plan progress is not critical to recording success
    }
  }, []);

  // Function to advance to next verses - checks plan subscription first
  const advanceToNextVerses = useCallback(async (useExistingData = false) => {
    // Prevent multiple simultaneous calls
    if (isAdvancingRef.current) {
      console.log('🔄 ADVANCING: Already advancing, skipping...');
      return;
    }
    
    isAdvancingRef.current = true;
    
    try {
      console.log('🔄 ADVANCING: Checking for plan subscription first...');
      
      // Check if user has a recording plan subscription
      const { collection, getDocs, doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../config/firebase');
      
      const recordingPlansSnapshot = await getDocs(
        collection(db, `users/${currentUser.uid}/recordingPlans`)
      );
      
      if (recordingPlansSnapshot.docs.length > 0) {
        // Check if user has any active plans
        const activePlans = recordingPlansSnapshot.docs.filter(doc => doc.data().status === 'active');
        
        if (activePlans.length > 0) {
          // User has an active plan subscription - advance to next plan verses
          console.log('📖 ADVANCING: User has active plan subscription, checking plan progress...');
          await advanceToNextPlanVerses(useExistingData);
        } else {
          // User has plans but they're all archived - use default logic
          console.log('📖 ADVANCING: User has archived plans, using default verse advancement...');
          setCurrentPlanName(null);
          await advanceToNextUnrecordedVerses(useExistingData);
        }
      } else {
        // No plan subscription - use default logic
        console.log('📖 ADVANCING: No plan subscription, using default verse advancement...');
        setCurrentPlanName(null);
        await advanceToNextUnrecordedVerses(useExistingData);
      }
    } catch (error) {
      console.error('❌ Error advancing to next verses:', error);
      // Fallback to default logic if there's an error
      await advanceToNextUnrecordedVerses(useExistingData);
    } finally {
      isAdvancingRef.current = false;
    }
  }, [currentUser, selectedBook, selectedChapter, startVerse, endVerse, existingRecordings, userHasManuallyChanged]);

  // Looks up the name of the user's active Chapter Plan, if any, WITHOUT
  // touching book/chapter/verse selection. advanceToNextVerses (above) is the
  // only other place currentPlanName gets set, but it's skipped whenever a
  // Passages Plan is already driving the page (see Record.js's main
  // auto-advance effect) — so without this, currentPlanName stays null and
  // the Record page's plan-switch toggle never appears for a user who
  // actually has both plan types, just because the Passages Plan won the
  // priority race.
  const peekActiveChapterPlanName = useCallback(async () => {
    if (!currentUser) return;
    try {
      const { collection, getDocs, doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../config/firebase');
      const recordingPlansSnapshot = await getDocs(
        collection(db, `users/${currentUser.uid}/recordingPlans`)
      );
      const activePlanDoc = recordingPlansSnapshot.docs.find(d => d.data().status === 'active');
      if (!activePlanDoc) {
        setCurrentPlanName(null);
        return;
      }
      const planDoc = await getDoc(doc(db, 'plans', activePlanDoc.data().planId));
      setCurrentPlanName(planDoc.exists() ? (planDoc.data().name || null) : null);
    } catch (error) {
      console.error('Error peeking active chapter plan name:', error);
    }
  }, [currentUser]);

  // Function to advance to next verses in the user's subscribed plan
  const advanceToNextPlanVerses = useCallback(async (useExistingData = false) => {
    try {
      console.log('📖 PLAN ADVANCING: Looking for next unrecorded verses in plan...');
      
      // Don't override manual selections
      if (userHasManuallyChanged) {
        console.log('📖 PLAN ADVANCING: User has manually changed selection, skipping plan advancement');
        return;
      }
      
      const { collection, getDocs, doc, getDoc, query, where } = await import('firebase/firestore');
      const { db } = await import('../config/firebase');
      
      // Get user's active recording plan
      const recordingPlansSnapshot = await getDocs(
        collection(db, `users/${currentUser.uid}/recordingPlans`)
      );
      
      const activePlans = recordingPlansSnapshot.docs.filter(doc => doc.data().status === 'active');

      if (activePlans.length === 0) {
        console.log('📖 PLAN ADVANCING: No active recording plan found, falling back to default logic');
        setCurrentPlanName(null);
        await advanceToNextUnrecordedVerses(useExistingData);
        return;
      }

      const activePlanDocRef = activePlans[0].ref;
      const recordingPlan = activePlans[0].data();
      console.log('📖 PLAN ADVANCING: Recording plan data:', recordingPlan);
      
      // Get the actual plan data
      const planDoc = await getDoc(doc(db, 'plans', recordingPlan.planId));
      if (!planDoc.exists()) {
        console.log('📖 PLAN ADVANCING: Plan document not found, falling back to default logic');
        setCurrentPlanName(null);
        await advanceToNextUnrecordedVerses(useExistingData);
        return;
      }
      
      const planData = planDoc.data();
      const planChapters = planData.chapters || [];
      console.log('📖 PLAN ADVANCING: Plan chapters:', planChapters);
      
      // Set the plan name for display
      setCurrentPlanName(planData.name || null);
      
      // Check if plan is already completed
      console.log('📖 PLAN ADVANCING: Plan status:', recordingPlan.status);
      if (recordingPlan.status === 'completed') {
        console.log('📖 PLAN ADVANCING: Plan is already completed, skipping advancement');
        return;
      }
      
      // Reset completion alert if this is a different plan than the last completed one
      if (lastCompletedPlanIdRef.current !== null && lastCompletedPlanIdRef.current !== recordingPlan.planId) {
        console.log('📖 PLAN ADVANCING: Different plan detected, resetting completion alert');
      }
      lastCompletedPlanIdRef.current = recordingPlan.planId;
      
      // Get family completion data to check which chapters are completed
      const { getFamilyCompletionData } = await import('../utils/familyCompletion');
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const userData = userDoc.data();
      const userFamilyIds = userData.familyIds || [];
      
      let familyCompletionData = null;
      if (userFamilyIds.length > 0) {
        // Get completion data from all user's families and merge them
        const allFamilyCompletionData = [];
        for (const familyId of userFamilyIds) {
          const familyData = await getFamilyCompletionData(familyId, db);
          if (familyData) {
            allFamilyCompletionData.push(familyData);
          }
        }
        
        // Merge completion data from all families
        if (allFamilyCompletionData.length > 0) {
          familyCompletionData = {
            chapters: [],
            totalChapters: 0,
            completedChapters: 0
          };
          
          // Merge chapters from all families, keeping the most complete status
          const chapterMap = new Map();
          for (const familyData of allFamilyCompletionData) {
            if (familyData.chapters) {
              for (const chapter of familyData.chapters) {
                const key = `${chapter.book}_${chapter.chapter}`;
                if (!chapterMap.has(key) || chapter.completed) {
                  chapterMap.set(key, chapter);
                }
              }
            }
          }
          
          familyCompletionData.chapters = Array.from(chapterMap.values());
          familyCompletionData.totalChapters = familyCompletionData.chapters.length;
          familyCompletionData.completedChapters = familyCompletionData.chapters.filter(ch => ch.completed).length;
        }
        
        console.log('📖 PLAN ADVANCING: Merged family completion data:', familyCompletionData);
      }
      
      // Check if all chapters are already completed before doing detailed checking
      let allChaptersCompleted = true;
      for (const planChapter of planChapters) {
        const chapterKey = `${planChapter.book}_${planChapter.chapter}`;

        // Check family completion data first, then fall back to plan's own completedChapters
        let isCompleted = false;
        if (familyCompletionData && familyCompletionData.chapters) {
          const chapterData = familyCompletionData.chapters.find(ch =>
            ch.book === planChapter.book && ch.chapter === planChapter.chapter
          );
          isCompleted = chapterData?.completed || false;
        }
        if (!isCompleted) {
          isCompleted = (recordingPlan.completedChapters || []).includes(chapterKey);
        }
        console.log(`📖 PLAN ADVANCING: Chapter ${chapterKey} completion status: ${isCompleted}`);

        if (!isCompleted) {
          allChaptersCompleted = false;
          break;
        }
      }
      
      // If all chapters are completed, show congratulations modal and archive the plan
      if (allChaptersCompleted) {
        console.log('🎉 PLAN ADVANCING: All plan chapters are completed!');
        
        // Check if this plan has already been archived by looking at the plan status
        if (recordingPlan.status !== 'archived') {
          console.log('🎉 PLAN ADVANCING: Showing congratulations modal and archiving plan');
          
          // Set the completed plan data for the modal
          setCompletedPlanData({
            planId: recordingPlan.planId,
            planName: planData.name,
            planDescription: planData.description,
            totalChapters: planChapters.length,
            completedChapters: planChapters.map(chapter => `${chapter.book}_${chapter.chapter}`)
          });
          
          // Show the completion modal
          setShowCompletionModal(true);
          
          // Archive the plan for this family
          console.log('🎉 PLAN ADVANCING: About to archive plan:', recordingPlan.planId);
          await archiveCompletedPlan(recordingPlan.planId, currentUser.uid, db);
          console.log('🎉 PLAN ADVANCING: Plan archiving completed');
        } else {
          console.log('🎉 PLAN ADVANCING: Plan already archived, skipping alert');
        }
        return; // Exit early when all chapters are completed
      }
      
      // Find the first uncompleted chapter in the plan
      for (const planChapter of planChapters) {
        const chapterKey = `${planChapter.book}_${planChapter.chapter}`;

        // Check family completion data first, then fall back to plan's own completedChapters
        let isCompleted = false;
        if (familyCompletionData && familyCompletionData.chapters) {
          const chapterData = familyCompletionData.chapters.find(ch =>
            ch.book === planChapter.book && ch.chapter === planChapter.chapter
          );
          isCompleted = chapterData?.completed || false;
        }
        if (!isCompleted) {
          isCompleted = (recordingPlan.completedChapters || []).includes(chapterKey);
        }
        console.log(`📖 PLAN ADVANCING: Chapter ${chapterKey} completion status: ${isCompleted}`);
        
        // If chapter is not completed, find the first unrecorded verse
        if (!isCompleted) {
          // Found an uncompleted chapter - check individual verse completion
          console.log(`✅ PLAN ADVANCING: Found next uncompleted chapter: ${chapterKey}`);
          
          const { getVersesForChapter } = await import('../utils/bibleData');
          const allVerses = getVersesForChapter(planChapter.book, planChapter.chapter);
          if (allVerses && allVerses.length > 0) {
            // Query for individual verse recordings in this chapter
            const recordingsSnapshot = await getDocs(
              query(
                collection(db, 'recordings'),
                where('book', '==', planChapter.book.toLowerCase()),
                where('chapter', '==', planChapter.chapter),
                where('userId', '==', currentUser.uid)
              )
            );
            
            const recordedVerses = new Set();
            recordingsSnapshot.docs.forEach(doc => {
              const data = doc.data();
              if (data.verse) {
                recordedVerses.add(data.verse);
              }
            });
            
            // Find first unrecorded verse
            const firstUnrecordedVerse = allVerses.find(verse => !recordedVerses.has(verse));

            if (firstUnrecordedVerse) {
              console.log(`✅ PLAN ADVANCING: Found first unrecorded verse: ${firstUnrecordedVerse}`);

              // Only change book/chapter if different from current selection
              if (planChapter.book !== selectedBook || planChapter.chapter !== selectedChapter) {
                setSelectedBook(planChapter.book);
                setSelectedChapter(planChapter.chapter);

                // Notify parent of verse range change
                if (onPlanAdvancement) {
                  onPlanAdvancement(planChapter.book, planChapter.chapter, firstUnrecordedVerse, allVerses[allVerses.length - 1]);
                }
              }

              setStartVerse(firstUnrecordedVerse);
              setEndVerse(allVerses[allVerses.length - 1]);
              return;
            } else {
              // All verses are recorded — heal the plan doc if completedChapters is stale
              if (!(recordingPlan.completedChapters || []).includes(chapterKey)) {
                console.log(`🔧 PLAN ADVANCING: Healing stale completedChapters — marking ${chapterKey} complete`);
                try {
                  const { updateDoc: ud, serverTimestamp: sts } = await import('firebase/firestore');
                  const healed = [...new Set([...(recordingPlan.completedChapters || []), chapterKey])];
                  await ud(activePlanDocRef, { completedChapters: healed, updatedAt: sts() });
                  recordingPlan.completedChapters = healed;
                } catch (healErr) {
                  console.warn('⚠️ Could not heal completedChapters:', healErr);
                }
              }
            }
          }
        }
      }
      
    } catch (error) {
      console.error('❌ Error advancing to next plan verses:', error);
      // Fallback to default logic if there's an error
      await advanceToNextUnrecordedVerses(useExistingData);
    }
  }, [currentUser, selectedBook, selectedChapter, startVerse, endVerse, existingRecordings, userHasManuallyChanged, onPlanAdvancement]);

  // Advance to the next unrecorded verses when no recording plan is active.
  // Honors the user's "Default Record Point" preference:
  //   'last-recorded' (default) -> continue from the chapter most recently recorded
  //   'whole-bible'             -> first unrecorded verse scanning Genesis -> Revelation
  const advanceToNextUnrecordedVerses = useCallback(async (useExistingData = false) => {
    try {
      // Don't override manual selections.
      if (userHasManuallyChanged) {
        console.log('🔄 ADVANCING: User manually changed selection, skipping');
        return;
      }

      const { collection, query, where, getDocs, doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../config/firebase');
      const { getVersesForChapter, BIBLE_BOOKS } = await import('../utils/bibleData');

      // Read the user's Default Record Point preference (defaults to last-recorded).
      let mode = 'last-recorded';
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        mode = userDoc.data()?.defaultRecordPoint || 'last-recorded';
      } catch (e) {
        console.warn('🔄 ADVANCING: could not read defaultRecordPoint, using last-recorded', e);
      }

      // Fetch all of the user's recordings once; build a verse-key set and find the latest.
      const recordedSet = new Set(); // `${book}_${chapter}_${verse}`
      let latest = null; // { book, chapter, t }
      const snap = await getDocs(
        query(collection(db, 'recordings'), where('userId', '==', currentUser.uid))
      );
      snap.docs.forEach((d) => {
        const r = d.data();
        if (!r.book || !r.chapter || !r.verse) return;
        const book = String(r.book).toLowerCase();
        recordedSet.add(`${book}_${r.chapter}_${r.verse}`);
        const t = r.createdAt && r.createdAt.toMillis
          ? r.createdAt.toMillis()
          : (r.createdAt ? new Date(r.createdAt).getTime() : 0);
        if (!latest || t > latest.t) latest = { book, chapter: r.chapter, t };
      });

      const firstGapIn = (book, chapter) => {
        const verses = getVersesForChapter(book, chapter);
        if (!verses || verses.length === 0) return null;
        const gap = verses.find((v) => !recordedSet.has(`${book}_${chapter}_${v}`));
        return gap === undefined ? null : gap;
      };

      const nextChapterAfter = (book, chapter) => {
        const idx = BIBLE_BOOKS.findIndex((b) => b.id === book);
        if (idx === -1) return null;
        if (chapter < BIBLE_BOOKS[idx].chapters) return { book, chapter: chapter + 1 };
        if (idx + 1 < BIBLE_BOOKS.length) return { book: BIBLE_BOOKS[idx + 1].id, chapter: 1 };
        return null;
      };

      let target = null; // { book, chapter, verse }

      if (mode === 'whole-bible') {
        // First unrecorded verse scanning the whole Bible in canonical order.
        for (const bk of BIBLE_BOOKS) {
          let found = false;
          for (let ch = 1; ch <= bk.chapters; ch++) {
            const gap = firstGapIn(bk.id, ch);
            if (gap != null) { target = { book: bk.id, chapter: ch, verse: gap }; found = true; break; }
          }
          if (found) break;
        }
      } else if (latest) {
        // Continue from the chapter most recently recorded.
        let book = latest.book;
        let chapter = latest.chapter;
        let gap = firstGapIn(book, chapter);
        while (gap == null) {
          const nxt = nextChapterAfter(book, chapter);
          if (!nxt) break;
          book = nxt.book;
          chapter = nxt.chapter;
          gap = firstGapIn(book, chapter);
        }
        if (gap == null) {
          const verses = getVersesForChapter(book, chapter);
          gap = (verses && verses.length) ? verses[0] : 1;
        }
        target = { book, chapter, verse: gap };
      }

      // Fallback (e.g. the user has never recorded): Genesis 1:1.
      if (!target) target = { book: 'genesis', chapter: 1, verse: 1 };

      const verses = getVersesForChapter(target.book, target.chapter);
      const lastVerse = (verses && verses.length) ? verses[verses.length - 1] : target.verse;

      console.log(`✅ ADVANCING (${mode}): ${target.book} ${target.chapter}:${target.verse}`);
      setSelectedBook(target.book);
      setSelectedChapter(target.chapter);
      setStartVerse(target.verse);
      setEndVerse(lastVerse);

      if (onPlanAdvancement) {
        onPlanAdvancement(target.book, target.chapter, target.verse, lastVerse);
      }
    } catch (error) {
      console.error('❌ Error advancing to next unrecorded verses:', error);
    }
  }, [currentUser, userHasManuallyChanged, onPlanAdvancement]);

  // Manual selection handlers that override plan logic
  const handleManualBookChange = useCallback((e) => {
    setSelectedBook(e.target.value);
    setUserHasManuallyChanged(true);
    setCurrentPlanName(null); // Clear plan name when manually changing
    lastCompletedPlanIdRef.current = null; // Reset plan tracking when manually changing
    chapterManuallyPickedRef.current = true; // default the verse range once this chapter's recordings load
  }, []);

  const handleManualChapterChange = useCallback((e) => {
    setSelectedChapter(parseInt(e.target.value));
    setUserHasManuallyChanged(true);
    setCurrentPlanName(null); // Clear plan name when manually changing
    lastCompletedPlanIdRef.current = null; // Reset plan tracking when manually changing
    chapterManuallyPickedRef.current = true; // default the verse range once this chapter's recordings load
  }, []);

  // Once the recordings for a manually-picked book/chapter have loaded,
  // default the verse range to [next unrecorded verse, last verse in chapter]
  // instead of leaving whatever start/end verse was set for the old chapter.
  useEffect(() => {
    if (!chapterManuallyPickedRef.current) return;
    const verses = getVersesForChapter(selectedBook, selectedChapter);
    if (!verses || verses.length === 0) return;
    const recordedVerseSet = new Set((existingRecordings || []).map((r) => Number(r.verse)));
    const gap = verses.find((v) => !recordedVerseSet.has(v));
    chapterManuallyPickedRef.current = false;
    setStartVerse(gap !== undefined ? gap : verses[0]);
    setEndVerse(verses[verses.length - 1]);
  }, [selectedBook, selectedChapter, existingRecordings, setStartVerse, setEndVerse]);

  const handleManualStartVerseChange = useCallback((e) => {
    setStartVerse(parseInt(e.target.value));
    setUserHasManuallyChanged(true);
    setCurrentPlanName(null); // Clear plan name when manually changing
    lastCompletedPlanIdRef.current = null; // Reset plan tracking when manually changing
  }, []);

  const handleManualEndVerseChange = useCallback((e) => {
    setEndVerse(parseInt(e.target.value));
    setUserHasManuallyChanged(true);
    setCurrentPlanName(null); // Clear plan name when manually changing
    lastCompletedPlanIdRef.current = null; // Reset plan tracking when manually changing
  }, []);

  // Reset to plan mode
  const handleFollowPlan = useCallback(async () => {
    setUserHasManuallyChanged(false);
    setCurrentPlanName(null);
    lastCompletedPlanIdRef.current = null; // Reset plan tracking when following plan
    await advanceToNextVerses(true);
  }, [advanceToNextVerses]);

  // Handle completion modal actions
  const handleCompletionModalClose = useCallback(async () => {
    try {
      // Unsubscribe user from recording plan
      const { db } = await import('../config/firebase');
      await unsubscribeFromRecordingPlan(currentUser.uid, db);
      
      // Close modal
      setShowCompletionModal(false);
      setCompletedPlanData(null);
      
      // Clear plan name
      setCurrentPlanName(null);
      
      console.log('✅ Completion modal closed and user unsubscribed from recording plan');
    } catch (error) {
      console.error('Error handling completion modal close:', error);
      setShowCompletionModal(false);
      setCompletedPlanData(null);
    }
  }, [currentUser]);

  const handleCompletionModalChooseNewPlan = useCallback(() => {
    // Close modal first
    setShowCompletionModal(false);
    setCompletedPlanData(null);
    
    // Navigate to profile page plans section
    // This will be handled by the parent component
    if (onPlanAdvancement) {
      onPlanAdvancement('navigate', 'profile', 'plans');
    }
  }, [onPlanAdvancement]);

  return {
    // State
    currentPlanName,
    userHasManuallyChanged,
    showCompletionModal,
    completedPlanData,
    
    // Setters
    setCurrentPlanName,
    setUserHasManuallyChanged,
    
    // Functions
    checkAndAwardBadges,
    updatePlanProgress,
    advanceToNextVerses,
    advanceToNextPlanVerses,
    advanceToNextUnrecordedVerses,
    peekActiveChapterPlanName,
    handleManualBookChange,
    handleManualChapterChange,
    handleManualStartVerseChange,
    handleManualEndVerseChange,
    handleFollowPlan,
    handleCompletionModalClose,
    handleCompletionModalChooseNewPlan
  };
};
